import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

interface RatingSliderProps {
  value: number;
  maxValue: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export const getSliderColor = (value: number, maxValue: number): string => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  
  if (percentage >= 90) return "bg-green-500";
  if (percentage >= 80) return "bg-green-400";
  if (percentage >= 70) return "bg-lime-400";
  if (percentage >= 60) return "bg-yellow-400";
  if (percentage >= 50) return "bg-amber-400";
  if (percentage >= 40) return "bg-orange-400";
  if (percentage >= 30) return "bg-orange-500";
  if (percentage >= 20) return "bg-red-400";
  if (percentage >= 10) return "bg-red-500";
  return "bg-red-600";
};

export const getSliderTextColor = (value: number, maxValue: number): string => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  
  if (percentage >= 90) return "text-green-600";
  if (percentage >= 80) return "text-green-500";
  if (percentage >= 70) return "text-lime-500";
  if (percentage >= 60) return "text-yellow-600";
  if (percentage >= 50) return "text-amber-600";
  if (percentage >= 40) return "text-orange-500";
  if (percentage >= 30) return "text-orange-600";
  if (percentage >= 20) return "text-red-400";
  if (percentage >= 10) return "text-red-500";
  return "text-red-600";
};

export const getRatingLabel = (value: number, maxValue: number): string => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  
  if (percentage === 100) return "Voll erfüllt";
  if (percentage >= 75) return "Überwiegend erfüllt";
  if (percentage >= 50) return "Teilweise erfüllt";
  return "Keine Erfüllung";
};

const RatingSlider = ({ value, maxValue, onChange, disabled = false }: RatingSliderProps) => {
  const colorClass = getSliderColor(value, maxValue);
  const textColorClass = getSliderTextColor(value, maxValue);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm",
            colorClass
          )}>
            {value}
          </div>
          <span className={cn("text-sm font-medium", textColorClass)}>
            / {maxValue} Punkte
          </span>
        </div>
        <span className={cn("text-xs font-medium", textColorClass)}>
          {getRatingLabel(value, maxValue)}
        </span>
      </div>
      <Slider
        value={[value]}
        max={maxValue}
        min={0}
        step={1}
        onValueChange={([newValue]) => onChange(newValue)}
        disabled={disabled}
        className={cn(
          "cursor-pointer",
          disabled && "cursor-not-allowed opacity-50"
        )}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>0</span>
        <span>{maxValue}</span>
      </div>
    </div>
  );
};

export default RatingSlider;
